import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {observer} from 'mobx-react';
import parse from 'html-react-parser';
import Voting from 'js/components/Common/Voting/Voting';
import Heading from 'js/components/Common/Heading/Heading';
import useFetchData from 'js/hooks/useFetchData/useFetchData';
import Feedback from 'js/components/Common/Feedback/Feedback';
import Container from 'js/components/Common/Container/Container';
import {CommonContext} from 'js/context/CommonContext/CommonContext';
import DataValidator from 'js/components/Common/DataValidator/DataValidator';
import MoreInfoBlock from 'js/containers/PostTypes/Articles/Content/MoreInfoBlock/MoreInfoBlock';
import {ImageBlockDataInterface} from 'js/components/Common/Image/ImageBlockDataInterface';

import fetchData from './fetchData';
import MainTab from './MainTab/MainTab';
import classes from './LearningPath.module.pcss';
import FilterTabs from './FilterTabs/FilterTabs';
import FilterContent from './FilterContent/FilterContent';
import LearningPathStore, {TabData} from 'js/store/LearningPathStore';


interface Props {
    pageId: number
}

export type FAQType = {
    ufid: string,
    caseHeader: string,
    caseBody: string
}

export type TabInfo = {
    icon: ImageBlockDataInterface,
    title: string,
    shortDescription: string,
    successMessage: string,
    completePopup: string,
    confirmCompletion: string
}

const LearningPath = ({pageId}: Props) => {
    const {apiPath, currentUser} = useContext(CommonContext);
    const ref = useRef(null);
    const [firstStartFilterTab, setFirstStartFilterTab] = useState<string>('');
    const [secondStartFilterTab, setSecondStartFilterTab] = useState<string>('');
    const [title, setTitle] = useState<string>('');
    const [pageTitle, setPageTitle] = useState<string>('');
    const [feedbackTitle, setFeedBackTitle] = useState<string>('');
    const [description, setDescriptions] = useState<string>('');
    const [firstTabInfo, setFirstTabInfo] = useState<TabInfo>(null);
    const [secondTabInfo, setSecondTabInfo] = useState<TabInfo>(null);
    const [firstTabData, setFirstTabData] = useState<TabData>(null);
    const [secondTabData, setSecondTabData] = useState<TabData>(null);
    const checkedAllInTab1 = LearningPathStore.getGroupCompletedStatus(LearningPathStore.faqStatusList, 1);
    const checkedAllInTab2 = LearningPathStore.getGroupCompletedStatus(LearningPathStore.faqStatusList, 2);

    const successTab1Message = useMemo(() =>
        `<span>Success, </span> ${currentUser?.firstName} ${currentUser?.lastName}! <br>${firstTabInfo?.successMessage}`
        , [currentUser, firstTabInfo]);

    const successTab2Message = useMemo(() =>
            `<span>Success,</span> ${currentUser?.firstName} ${currentUser?.lastName}! <br>${secondTabInfo?.successMessage}`
        , [currentUser, secondTabInfo]);


    const {isLoading, error} = useFetchData({
        query: `${fetchData(pageId)}`,
        condition: !!pageId,
        updateData: (data: any): void => {
            const main = data?.post?.SelfLearning;
            setTitle(main?.titleSelfLearning);
            setPageTitle(data?.post?.title);
            setDescriptions(main?.descriptionSelfLearning);
            setFirstTabInfo(main?.mainTabs?.firstItem);
            setSecondTabInfo(main?.mainTabs?.secondItem);
            setFirstTabData(main?.firstTabData);
            setSecondTabData(main?.secondTabData);
            LearningPathStore.setFAQStatusList(data?.selfLearning?.getStatusById);
            if (main?.firstTabData?.filterTabs?.length > 0) {
                setFirstStartFilterTab(main?.firstTabData?.filterTabs[0]?.ufid);
            }
            if (main?.secondTabData?.filterTabs?.length > 0) {
                setSecondStartFilterTab(main?.secondTabData?.filterTabs[0]?.ufid);
            }
            setFeedBackTitle(data?.monday?.Monday?.feedback?.articleFeedbackTitle);
        }
    });

    useEffect(() => {
        LearningPathStore.setFirstTabData(firstTabData);
    }, [firstTabData]);

    useEffect(() => {
        LearningPathStore.setSecondTabData(secondTabData);
    }, [secondTabData]);

    useEffect(() => {
        if (firstStartFilterTab) {
            LearningPathStore.setActiveFilterTab(firstStartFilterTab);
            LearningPathStore.setActiveTabData('first_item');
        } else {
            LearningPathStore.setActiveFilterTab(secondStartFilterTab);
            LearningPathStore.setActiveTabData('second_item');
        }
        LearningPathStore.setActiveFAQContent();
    }, [firstStartFilterTab, secondStartFilterTab]);

    const mainTabClickHandler = useCallback((index : 'first_item' | 'second_item') => {
        LearningPathStore.lastClickedElement = '';
        LearningPathStore.setActiveMainTab(index);
        LearningPathStore.setActiveTabData(index);
        if (index === 'second_item') {
            LearningPathStore.setActiveFilterTab(secondStartFilterTab);
            LearningPathStore.setActiveFAQContent();
            return;
        }
        LearningPathStore.setActiveFilterTab(firstStartFilterTab);
        LearningPathStore.setActiveFAQContent();
    }, [firstStartFilterTab, secondStartFilterTab]);

    return (
        <DataValidator data={!isLoading && !error}>
            <section className={classes.LearningPath}>
                <div className={classes.Top}>
                    <Container additionalClass={classes.ContainerWrap}>
                        <Heading text={title} />
                        <div className={classes.Description}>{parse(description || '')}</div>
                        <div className={classes.MainTabs}>
                            {
                            !!LearningPathStore.firstTabData?.filterTabs?.length ?
                                <MainTab
                                    index={'first_item'}
                                    disabled={!LearningPathStore.firstTabData?.filterTabs?.length}
                                    clickHandler={mainTabClickHandler}
                                    title={firstTabInfo?.title}
                                    description={firstTabInfo?.shortDescription}
                                    image={firstTabInfo?.icon?.mediaItemUrl} /> : ''
                            }
                            {
                            !!LearningPathStore.secondTabData?.filterTabs?.length ?
                            <MainTab
                                index={'second_item'}
                                disabled={!LearningPathStore.secondTabData?.filterTabs?.length}
                                clickHandler={mainTabClickHandler}
                                title={secondTabInfo?.title}
                                description={secondTabInfo?.shortDescription}
                                image={secondTabInfo?.icon?.mediaItemUrl} /> : ''}
                        </div>
                    </Container>
                </div>
                <div className={classes.Bottom}>
                    <Container additionalClass={classes.ContainerWrap}>
                        {checkedAllInTab1 && LearningPathStore.activeMainTab === 'first_item' ?
                            <Heading text={successTab1Message} type={'h5'} /> : ''
                        }
                        {checkedAllInTab2 && LearningPathStore.activeMainTab === 'second_item' ?
                            <Heading text={successTab2Message} type={'h5'} /> : ''
                        }
                        <div className={classes.ActiveTabDescription}>
                            {parse(LearningPathStore.activeTabData?.description || '')}
                        </div>
                        <FilterTabs />
                        <FilterContent
                            firstTabInfo={firstTabInfo}
                            secondTabInfo={secondTabInfo}
                            nodeRef={ref}
                            postId={pageId} />
                        <div className={classes.Wrap}>
                            <Feedback
                                id={pageId}
                                label={feedbackTitle}
                                title={pageTitle}
                                eventCategory={'Self-Learning page'}
                                link={`${apiPath}${location?.pathname}`}
                            />
                            <Voting
                                title={pageTitle}
                                eventCategory={'Self-Learning page'}
                            />
                        </div>
                        <MoreInfoBlock type={'furtherAssistanceInfo'} />
                    </Container>
                </div>
            </section>
        </DataValidator>
    );
};

export default observer(LearningPath);
